import React from "react";
import { Drizzle } from "@/components/guide/Drizzle";
import {
  CoverScene,
  ModelScene,
  SpaceScene,
  SpacesScene,
} from "./HierarchyPreviews";
import { laidOver, vars, type Box } from "@/components/guide/story";
import { FIRST_ROW, HOLDER, MARKED } from "./boxes";
import "@/components/guide/story.css";

// Getting started's pictures, one story: each chapter a close-up of a box in
// the one before, which grows into its panel going forward and folds back going back.

interface Opening {
  from: Box;
  fill: string;
  stroke: string;
  to: Box;
  toFill: string;
  toStroke: string;
  /** It lands as the chapter's own box (`og-holder`), not drawn again. */
  isPanel: boolean;
  /** Where the box's title stood, from where the panel's title stands. */
  title: { dx: number; dy: number };
}

interface Chapter {
  Scene: () => React.JSX.Element;
  /** The box of the chapter before, and where it lands in this one. */
  opens?: Opening;
}

const PANEL = { fill: "var(--color-bg-subtle)", stroke: "var(--color-border)" };
// A row's title sits 8 into the row; a panel's at its head.
const ROW_TITLE = { dx: 0, dy: FIRST_ROW.y + 5 - HOLDER.y - 8 };
const closeUp = (from: Box, title: Opening["title"]): Opening => ({
  from,
  ...MARKED,
  to: HOLDER,
  toFill: PANEL.fill,
  toStroke: PANEL.stroke,
  isPanel: true,
  title,
});

const CHAPTERS: Chapter[] = [
  { Scene: CoverScene },
  {
    Scene: SpacesScene,
    // The space steps back into the list of spaces, settling unmarked into
    // its first row: it is chosen only once the list has been taken in.
    opens: {
      from: HOLDER,
      ...PANEL,
      to: FIRST_ROW,
      toFill: "transparent",
      toStroke: "transparent",
      isPanel: false,
      title: { dx: 0, dy: 0 },
    },
  },
  { Scene: SpaceScene, opens: closeUp(FIRST_ROW, ROW_TITLE) },
  { Scene: ModelScene, opens: closeUp(FIRST_ROW, ROW_TITLE) },
];

const titleFrom = ({ title }: Opening) =>
  vars({ "--og-title-from": `translate(${title.dx}px, ${title.dy}px)` });

/** A panel that opens out of the marked box (forward) or folds into it (back). */
function Panel({
  opening,
  folds,
}: {
  opening: Opening;
  folds?: boolean;
}): React.JSX.Element {
  const { from, to } = opening;
  const motion: Record<string, string> = folds
    ? { "--og-to": laidOver(from, to) }
    : {
        "--og-from": laidOver(from, to),
        "--og-fill-from": opening.fill,
        "--og-stroke-from": opening.stroke,
      };
  return (
    <rect
      className={folds ? "og-panel og-panel--folds" : "og-panel"}
      style={{
        ...vars(motion),
        fill: opening.toFill,
        stroke: opening.toStroke,
      }}
      x={to.x}
      y={to.y}
      width={to.w}
      height={to.h}
      vectorEffect="non-scaling-stroke"
    />
  );
}

/** One chapter of the story; the tour's `data-came` decides how it begins. */
export function GuideStory({
  chapter,
}: {
  chapter: number;
}): React.JSX.Element {
  const { Scene, opens } = CHAPTERS[chapter];
  const Before = CHAPTERS[chapter - 1]?.Scene;
  const after = CHAPTERS[chapter + 1];
  return (
    <svg
      className="og-scene block w-full h-full"
      viewBox="0 0 320 180"
      aria-hidden="true"
    >
      <Drizzle />
      {Before && (
        <g className="og-forward">
          <g className="og-past">
            <Before />
          </g>
          {opens && <Panel opening={opens} />}
        </g>
      )}
      <g className="og-now">
        <g
          className={
            opens
              ? `og-opened${opens.isPanel ? " og-opened--panel" : ""}`
              : undefined
          }
          style={opens && titleFrom(opens)}
        >
          <Scene />
        </g>
      </g>
      {after && (
        <g
          className={`og-back${after.opens?.isPanel ? " og-back--panel" : ""}`}
          style={after.opens && titleFrom(after.opens)}
        >
          {after.opens && <Panel opening={after.opens} folds />}
          <g className="og-past">
            <after.Scene />
          </g>
        </g>
      )}
    </svg>
  );
}
